import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Input,
  LightMode,
  Text,
  useColorModeValue as mode,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Organization } from 'services/organization';

interface DeleteCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (organization: Organization) => void;
  organization: Organization | null;
}

const DeleteCompanyModal: React.FC<DeleteCompanyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  organization,
}) => {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Call all mode functions unconditionally at the top level
  const textColor = mode('gray.800', 'white');
  const secondaryTextColor = mode('gray.600', 'gray.300');
  const inputBg = mode('white', 'gray.700');
  const inputColor = mode('black', 'white');
  const inputBorderColor = mode('gray.300', 'gray.500');
  const placeholderColor = mode('gray.500', 'gray.400');
  const buttonColor = mode('black', 'white');
  const buttonBg = mode('gray.100', 'gray.600');
  const buttonHoverBg = mode('gray.200', 'gray.500');

  const handleConfirm = () => {
    if (!organization) return;

    if (confirmName !== organization.name) {
      toast({
        title: t('managementSystem.toast.error'),
        description: 'Organization name does not match. Please enter the correct name.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    onConfirm(organization);
    onClose();
    setConfirmName('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setConfirmName('');
    onClose();
  };

  // Don't render anything if modal is not open or no organization
  if (!isOpen || !organization) {
    return null;
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      returnFocusOnClose={false}
      size={{ base: 'sm', lg: 'lg' }}
    >
      <AlertDialogOverlay>
        <AlertDialogContent pos='absolute' top='23%' color='color_main_text' bg='bg_box'>
          <AlertDialogHeader
            color='red.500'
            fontSize={{ base: 'medium', md: 'large' }}
            fontWeight='bold'
          >
            {t('managementSystem.deleteCompany.title')}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align='stretch' spacing={4}>
              <Text color={textColor}>
                {t('managementSystem.deleteCompany.confirmMessage')}{' '}
                <Text as='span' color='red.500' fontWeight='bold'>
                  {organization.name}
                </Text>
                ?
              </Text>
              <Text color={secondaryTextColor} fontSize='sm'>
                {t('managementSystem.deleteCompany.warningMessage')}
              </Text>
              <Input
                color={inputColor}
                bg={inputBg}
                borderColor={inputBorderColor}
                _focus={{ borderColor: 'red.500' }}
                _placeholder={{ color: placeholderColor }}
                onChange={(e) => setConfirmName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
                placeholder={t('managementSystem.deleteCompany.placeholder', {
                  name: organization.name,
                })}
                value={confirmName}
              />
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <LightMode>
              <Button
                ref={cancelRef}
                ml={3}
                color={buttonColor}
                bg={buttonBg}
                _hover={{ bg: buttonHoverBg }}
                onClick={handleClose}
              >
                {t('managementSystem.deleteCompany.cancel')}
              </Button>
              <Button
                ml={3}
                colorScheme='red'
                isDisabled={confirmName !== organization.name}
                isLoading={isSubmitting}
                onClick={handleConfirm}
              >
                {t('managementSystem.deleteCompany.deleteButton')}
              </Button>
            </LightMode>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteCompanyModal;
